/**
 * Comprehensive Patient Management API Service
 * Handles all patient management features including profile, medical history, 
 * documents, consent management, and privacy controls
 */
import axios from 'axios';
import { API_BASE_URL } from '../../config';

// Use proxy for development, fallback to config
const BASE_URL = process.env.NODE_ENV === 'development' ? '' : API_BASE_URL;
const PATIENT_API = `${BASE_URL}/api/v1/patient`;

// Patient Profile API
export const patientProfileApi = {
  // Get patient profile
  getProfile: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/profile/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching patient profile:', error);
      throw error;
    }
  },

  // Update patient profile
  updateProfile: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/profile/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating patient profile:', error);
      throw error;
    }
  }
};

// Medical History API
export const medicalHistoryApi = {
  // Get medical history
  getMedicalHistory: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/medical-history/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching medical history:', error);
      throw error;
    }
  },

  // Create medical history entry
  createMedicalHistory: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/medical-history/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating medical history:', error);
      throw error;
    }
  },

  // Update medical history entry
  updateMedicalHistory: async (id, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${PATIENT_API}/medical-history/${id}/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating medical history:', error);
      throw error;
    }
  },

  // Delete medical history entry
  deleteMedicalHistory: async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.delete(`${PATIENT_API}/medical-history/${id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting medical history:', error);
      throw error;
    }
  }
};

// Document Management API
export const documentApi = {
  // Get documents
  getDocuments: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/documents/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  // Upload document
  uploadDocument: async (formData) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/documents/upload/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // Download document
  downloadDocument: async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/documents/${id}/download/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  // Preview document
  previewDocument: async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/documents/${id}/preview/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing document:', error);
      throw error;
    }
  },

  // Delete document
  deleteDocument: async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.delete(`${PATIENT_API}/documents/${id}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }
};

// Consent Management API
export const consentApi = {
  // Get consents
  getConsents: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/consents/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consents:', error);
      throw error;
    }
  },

  // Grant consent
  grantConsent: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/consents/grant/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error granting consent:', error);
      throw error;
    }
  },

  // Withdraw consent
  withdrawConsent: async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/consents/${id}/withdraw/`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error withdrawing consent:', error);
      throw error;
    }
  },

  // Get consent status
  getConsentStatus: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/consents/status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching consent status:', error);
      throw error;
    }
  }
};

// Privacy Controls API
export const privacyApi = {
  // Get privacy settings
  getPrivacySettings: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/privacy/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching privacy settings:', error);
      throw error;
    }
  },

  // Update privacy settings
  updatePrivacySettings: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/privacy/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating privacy settings:', error);
      throw error;
    }
  },

  // Export data
  exportData: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/privacy/export/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  },

  // Delete data
  deleteData: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/privacy/delete/`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting data:', error);
      throw error;
    }
  },

  // Get access logs
  getAccessLogs: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/privacy/access-log/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching access logs:', error);
      throw error;
    }
  }
};

// Communication API
export const communicationApi = {
  // Get messages
  getMessages: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/messages/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  // Get message thread
  getMessageThread: async (threadId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/messages/${threadId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching message thread:', error);
      throw error;
    }
  },

  // Send message
  sendMessage: async (data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/messages/send/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }
};

// Feedback API
export const feedbackApi = {
  // Get feedback
  getFeedback: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/feedback/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },

  // Submit feedback
  submitFeedback: async (doctorId, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${PATIENT_API}/feedback/doctor/${doctorId}/`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }
};

// Billing API
export const billingApi = {
  // Get invoices
  getInvoices: async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/invoices/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Get invoice detail
  getInvoiceDetail: async (invoiceId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/invoices/${invoiceId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice detail:', error);
      throw error;
    }
  },

  // Download invoice PDF
  downloadInvoicePDF: async (invoiceId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${PATIENT_API}/invoices/${invoiceId}/pdf/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      throw error;
    }
  }
};

export default {
  patientProfileApi,
  medicalHistoryApi,
  documentApi,
  consentApi,
  privacyApi,
  communicationApi,
  feedbackApi,
  billingApi
};
